/**
 * Sortable item list.
 *
 * Two behaviours carried over from the desktop app because they were fixed
 * there for good reasons:
 *
 * - Numeric columns sort numerically (issue I-51: `$8` must not sort above
 *   `$100`).
 * - Out-of-stock rows are greyed and pinned to the bottom whichever way the
 *   list is sorted (issue I-48).
 */

import { useMemo, useState } from 'react';

import type { Item } from '../lib/api';

type Column = 'upc' | 'name' | 'price' | 'count' | 'sold' | 'rank';

const COLUMNS: { key: Column; label: string; numeric: boolean }[] = [
  // This column is the item's internal id, not the manufacturer's UPC.
  { key: 'upc', label: 'ID', numeric: true },
  { key: 'name', label: 'Name', numeric: false },
  { key: 'price', label: 'Price', numeric: true },
  { key: 'count', label: 'Unit Stock', numeric: true },
  { key: 'sold', label: 'Sold', numeric: true },
  { key: 'rank', label: 'Rank', numeric: false },
];

interface Props {
  entries: [string, Item][];
  loading: boolean;
  selected: string | null;
  onSelect: (upc: string) => void;
}

export default function ItemTable({ entries, loading, selected, onSelect }: Props) {
  const [sortKey, setSortKey] = useState<Column>('name');
  const [ascending, setAscending] = useState(true);

  const sorted = useMemo(() => {
    const column = COLUMNS.find((entry) => entry.key === sortKey);
    const rows = [...entries];

    rows.sort(([upcA, a], [upcB, b]) => {
      // Out of stock always sinks, regardless of sort direction. Infinite
      // items are never "out", however their count reads.
      const outA = !a.infinite && Number(a.count ?? 0) === 0;
      const outB = !b.infinite && Number(b.count ?? 0) === 0;
      if (outA !== outB) return outA ? 1 : -1;

      const rawA = sortKey === 'upc' ? upcA : (a[sortKey] ?? '');
      const rawB = sortKey === 'upc' ? upcB : (b[sortKey] ?? '');

      let comparison: number;
      if (column?.numeric) {
        comparison = Number(rawA) - Number(rawB);
        if (Number.isNaN(comparison)) comparison = String(rawA).localeCompare(String(rawB));
      } else {
        comparison = String(rawA).localeCompare(String(rawB), undefined, {
          sensitivity: 'base',
        });
      }
      return ascending ? comparison : -comparison;
    });

    return rows;
  }, [entries, sortKey, ascending]);

  function toggleSort(key: Column) {
    if (key === sortKey) {
      setAscending((value) => !value);
    } else {
      setSortKey(key);
      setAscending(true);
    }
  }

  if (loading) return <p className="muted">Loading inventory…</p>;
  if (entries.length === 0) return <p className="muted">No items match.</p>;

  return (
    <div className="table-wrap">
      <table className="items">
        <thead>
          <tr>
            {COLUMNS.map((column) => (
              <th key={column.key}>
                <button type="button" className="sort" onClick={() => toggleSort(column.key)}>
                  {column.label}
                  {sortKey === column.key ? (ascending ? ' ▲' : ' ▼') : ''}
                </button>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map(([upc, item]) => {
            const out = !item.infinite && Number(item.count ?? 0) === 0;
            const classes = [
              out ? 'out-of-stock' : '',
              selected === upc ? 'selected' : '',
            ]
              .filter(Boolean)
              .join(' ');
            return (
              <tr
                key={upc}
                className={classes}
                onClick={() => onSelect(upc)}
                // A row is the primary control on this screen, so it has to be
                // reachable and activatable without a mouse.
                tabIndex={0}
                role="button"
                aria-pressed={selected === upc}
                aria-label={`${item.name}, $${Number(item.price ?? 0).toFixed(2)}`}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onSelect(upc);
                  }
                }}
              >
                <td className="mono">{upc}</td>
                <td>
                  {item.name}
                  {(item.variants?.length ?? 0) > 0 && (
                    <span className="chip" title="Has grab-bag variants">
                      {item.variants?.length} variants
                    </span>
                  )}
                </td>
                <td className="num">${Number(item.price ?? 0).toFixed(2)}</td>
                <td className="num">{item.infinite ? '∞' : item.count}</td>
                <td className="num">{item.sold ?? 0}</td>
                <td>{item.rank ?? '-'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
